import { type FC, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import FastTimer from 'dibs-icons/exports/FastTimer';
import Offer from 'dibs-icons/exports/Offer';

import { ItemPrice } from './ItemPrice';
import { IncludedLabels } from './IncludedLabels';
import { UrgencyBanner } from '../UrgencyBanner';
import { SalePriceBanner } from '../SalePriceBanner';
import { useItemPriceData } from '../hooks/useItemPriceData';
import { useShowIncludedLabelsInline } from './hooks/useShowIncludedLabelsInline';

import { type PriceDisplayVariant_item$key } from './__generated__/PriceDisplayVariant_item.graphql';
import { type PriceDisplayVariant_displayPrice$key } from './__generated__/PriceDisplayVariant_displayPrice.graphql';

const itemFragment = graphql`
    fragment PriceDisplayVariant_item on Item {
        displayInfo(page: pdp, buyerId: $userId) {
            ctas {
                showAcceptOffer
            }
        }
        ...ItemPrice_item
        ...useItemPriceData_item
        ...IncludedLabels_item
        ...useShowIncludedLabelsInline_item
    }
`;

const displayPriceFragment = graphql`
    fragment PriceDisplayVariant_displayPrice on ItemDisplayPriceType @relay(plural: true) {
        ...ItemPrice_displayPrice
        ...useItemPriceData_displayPrice
        ...IncludedLabels_displayPrice
        ...useShowIncludedLabelsInline_displayPrice
    }
`;

const bannerConfig = {
    hold: {
        Icon: FastTimer,
        iconColor: dibsCss.fillNoir,
        backgroundColor: dibsCss.bgMoonstone,
    },
    privateOffer: {
        Icon: Offer,
        iconColor: dibsCss.fillSuccesssecondary,
        backgroundColor: dibsCss.bgGreen50,
    },
};

const ExpiringBanner: FC<{
    type: 'hold' | 'privateOffer';
    displayMessage: ReactNode;
    expiration?: string | null;
}> = ({ type, displayMessage, expiration }) => {
    const config = bannerConfig[type];

    return (
        <UrgencyBanner backgroundColor={config.backgroundColor}>
            <span className={classnames(dibsCss.mrXsmall, config.iconColor)}>
                <config.Icon variation="20" />
            </span>
            <span>{displayMessage}</span>
            {expiration && (
                <span className={classnames(dibsCss.textBodySmallHeavy, dibsCss.mlXxsmall)}>
                    <FormattedMessage
                        id="PriceDisplayVariant.expiration"
                        defaultMessage="Expires {time}"
                        values={{
                            time: expiration,
                        }}
                    />
                </span>
            )}
        </UrgencyBanner>
    );
};

export const PriceDisplayVariant: FC<{
    item?: PriceDisplayVariant_item$key | null;
    displayPrice?: PriceDisplayVariant_displayPrice$key | null;
    currency: string;
    isCondensed?: boolean;
}> = ({ item: itemRef, displayPrice: displayPriceRef, currency, isCondensed }) => {
    const item = useFragment(itemFragment, itemRef);
    const displayPrice = useFragment(displayPriceFragment, displayPriceRef);
    const showIncludedLabelsInline = useShowIncludedLabelsInline({
        item,
        displayPrice,
        currency,
    });
    const priceData = useItemPriceData({
        item,
        displayPrice,
        currency,
    });

    if (!priceData) {
        return null;
    }

    const { holdType, holdExpiration, privateOfferExpiration, currentPrice } = priceData;
    const { showAcceptOffer } = item?.displayInfo?.ctas || {};

    return (
        <div data-tn="price-wrapper" className={dibsCss.wFull}>
            {!isCondensed &&
                !holdType &&
                !showAcceptOffer &&
                currentPrice.type === 'WEEKLY_SALE' &&
                'saleEndDate' in priceData && (
                    <SalePriceBanner saleEndDate={priceData.saleEndDate} />
                )}

            {showAcceptOffer && (
                <ExpiringBanner
                    type="privateOffer"
                    displayMessage={
                        <FormattedMessage
                            id="PriceDisplayVariant.privateOffer"
                            defaultMessage="You received an offer!"
                        />
                    }
                    expiration={privateOfferExpiration}
                />
            )}

            {holdType && (
                <ExpiringBanner
                    type="hold"
                    displayMessage={
                        holdType === 'HOLD' ? (
                            <FormattedMessage
                                id="PriceDisplayVariant.holdType"
                                defaultMessage="On Hold"
                            />
                        ) : (
                            <FormattedMessage
                                id="PriceDisplayVariant.holdTypeCustom"
                                defaultMessage="On Hold For You!"
                            />
                        )
                    }
                    expiration={holdType === 'HOLD_CUSTOM' ? holdExpiration : null}
                />
            )}

            <ItemPrice item={item} displayPrice={displayPrice} currency={currency} />

            {showIncludedLabelsInline && (
                <IncludedLabels item={item} displayPrice={displayPrice} currency={currency} />
            )}
        </div>
    );
};
